"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { can, useEmpresa, useSession } from "@/core/session";
import { HOME_ID, abaAtiva, openScreen, useTabs, type Tab } from "@/core/tabs";
import { escreverUrl, rotaAtual } from "@/core/rotas";
import { dispatchToTab, TabContext } from "@/core/commands";
import { SCREEN_META } from "@/core/screen-meta";
import { isTypingTarget } from "@/lib/utils";
import { useOnline } from "@/lib/use-now";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/overlay";
import { ErrorState } from "@/components/misc";
import { Spinner } from "@/components/ui/spinner";
import { AppMenubar } from "./Menubar";
import { WorkspaceTabs } from "./WorkspaceTabs";
import { StatusBar } from "./StatusBar";
import { CommandPalette, ShortcutsDialog } from "./CommandPalette";
import { SCREENS } from "./screens";
import { useUi } from "./ui-store";
import { EncerrarViagemDialog } from "@/features/mdfe/encerrar-dialog";
import { useGateAssinatura } from "@/core/conta";
import { AvisoAssinatura } from "@/features/assinatura/aviso-assinatura";
import { AvisoEmail } from "@/features/auth/aviso-email";

export function ErpWindow() {
  const router = useRouter();
  const token = useSession((s) => s.token);
  const usuario = useSession((s) => s.usuario);
  const empresaId = useSession((s) => s.empresaId);
  const empresa = useEmpresa();
  const userKey = useTabs((s) => s.userKey);
  const qc = useQueryClient();
  const bloqueada = useGateAssinatura();

  React.useEffect(() => {
    if (!token) {
      qc.removeQueries();
      router.replace("/login");
    } else if (!empresaId) router.replace("/empresa");
  }, [token, empresaId, router, qc]);

  React.useEffect(() => {
    if (usuario && userKey !== usuario.id) useTabs.getState().restore(usuario.id);
  }, [usuario, userKey]);

  useGlobalShortcuts();

  // URL ⇄ aba ativa. Entrada: link direto/recarregar abre a tela da URL; depois, trocar/abrir/fechar aba
  // escreve a URL (pushState) e voltar/avançar do navegador ativa a aba correspondente.
  React.useEffect(() => {
    if (!usuario || userKey !== usuario.id) return;
    let restaurando = true; // enquanto true, a URL é escrita com replace (não cria entrada no histórico)
    const r = rotaAtual();
    if (r && (r.screen !== "inicio" || Object.keys(r.params).length)) openScreen(r.screen, r.params);
    const sincronizar = () => {
      const t = abaAtiva();
      escreverUrl(t.screen, t.params, restaurando ? "replace" : "push");
    };
    sincronizar();
    restaurando = false;
    const unsub = useTabs.subscribe((s, prev) => {
      if (s.activeId !== prev.activeId || s.tabs !== prev.tabs) sincronizar();
    });
    const onPop = () => {
      const alvo = rotaAtual();
      if (!alvo) return;
      restaurando = true;
      try {
        openScreen(alvo.screen, alvo.params);
        sincronizar();
      } finally {
        restaurando = false;
      }
    };
    window.addEventListener("popstate", onPop);
    return () => {
      unsub();
      window.removeEventListener("popstate", onPop);
    };
  }, [usuario, userKey]);

  // Conta sem plano: abre (e foca) a aba Assinatura ao lado do Início
  React.useEffect(() => {
    if (bloqueada && usuario && userKey === usuario.id) openScreen("assinatura");
  }, [bloqueada, usuario, userKey]);

  if (!token || !empresa || !usuario || userKey !== usuario.id) {
    return (
      <div className="flex h-full items-center justify-center text-muted">
        <Spinner className="size-4" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {empresa.ambiente === "homologacao" ? <div data-print-hide aria-hidden className="h-[3px] shrink-0 bg-env-homolog" /> : null}
      <AvisoEmail />
      <AvisoAssinatura />
      <AppMenubar />
      <WorkspaceTabs />
      <TabHost />
      <StatusBar />
      {/* Sem plano, a busca não abre outras telas (os menus também ficam desabilitados) */}
      {bloqueada ? null : <CommandPalette />}
      <ShortcutsDialog />
      <ConfirmCloseDialog />
      <EncerrarViagemDialog />
    </div>
  );
}

/* ---------- Abas montadas (estado preservado ao alternar) ---------- */

function TabHost() {
  const tabs = useTabs((s) => s.tabs);
  const activeId = useTabs((s) => s.activeId);
  const online = useOnline();
  return (
    <main className="relative min-h-0 flex-1 bg-app">
      {tabs.map((t) => (
        <TabPane key={t.id} tab={t} active={t.id === activeId} />
      ))}
      {!online ? (
        <div className="absolute inset-0 z-20 bg-app">
          <ErrorState code="offline" />
        </div>
      ) : null}
    </main>
  );
}

const TabPane = React.memo(function TabPane({ tab, active }: { tab: Tab; active: boolean }) {
  const papel = useSession((s) => s.usuario?.papel);
  const ctx = React.useMemo(() => ({ tab, active }), [tab, active]);
  const Screen = SCREENS[tab.screen];
  const acao = SCREEN_META[tab.screen]?.acao;
  const negado = acao && !can(papel, acao);
  return (
    <div role="tabpanel" hidden={!active} className="absolute inset-0 flex flex-col overflow-hidden">
      <TabContext.Provider value={ctx}>
        <ScreenBoundary key={tab.nonce}>
          {negado ? <ErrorState code="403" /> : Screen ? <Screen params={tab.params} /> : <ErrorState code="404" />}
        </ScreenBoundary>
      </TabContext.Provider>
    </div>
  );
});

class ScreenBoundary extends React.Component<{ children: React.ReactNode }, { error: boolean }> {
  state = { error: false };
  static getDerivedStateFromError() {
    return { error: true };
  }
  componentDidCatch(e: unknown) {
    console.error(e);
  }
  render() {
    return this.state.error ? <ErrorState code="500" onRetry={() => this.setState({ error: false })} /> : this.props.children;
  }
}

/* ---------- Confirmação ao fechar aba alterada ---------- */

function ConfirmCloseDialog() {
  const ids = useTabs((s) => s.confirmClose);
  const tabs = useTabs((s) => s.tabs);
  const nomes = tabs.filter((t) => ids?.includes(t.id) && t.dirty).map((t) => t.title);
  return (
    <Dialog open={!!ids} onOpenChange={(v) => !v && useTabs.getState().cancelConfirm()}>
      <DialogContent
        title="Descartar alterações?"
        width={400}
        footer={
          <>
            <Button onClick={() => useTabs.getState().cancelConfirm()}>Voltar</Button>
            <Button variant="primary" onClick={() => ids && useTabs.getState().closeMany(ids, true)} autoFocus>
              Descartar
            </Button>
          </>
        }
      >
        <p>{nomes.length === 1 ? `"${nomes[0]}" tem alterações não salvas.` : `${nomes.length} abas têm alterações não salvas.`}</p>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Atalhos globais ---------- */

function useGlobalShortcuts() {
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tabs = useTabs.getState();
      const ui = useUi.getState();
      const papel = useSession.getState().usuario?.papel;
      const typing = isTypingTarget(e.target);
      const overlay = !!document.querySelector('[role="dialog"], [role="menu"], [role="listbox"]');
      const ctrl = e.ctrlKey || e.metaKey;
      const k = e.key;

      if (k === "F2") {
        e.preventDefault();
        if (can(papel, "viagem.emitir")) openScreen("nova-viagem");
        return;
      }
      if (k === "F3" || (ctrl && (k === "k" || k === "K"))) {
        e.preventDefault();
        ui.setPalette(true);
        return;
      }
      if (k === "F5") {
        e.preventDefault();
        dispatchToTab(tabs.activeId, "refresh");
        return;
      }
      if (k === "F10") {
        e.preventDefault();
        if (!overlay) dispatchToTab(tabs.activeId, "save");
        return;
      }
      if ((ctrl && (k === "w" || k === "W")) || (e.altKey && (k === "w" || k === "W"))) {
        e.preventDefault();
        if (tabs.activeId !== HOME_ID) tabs.close(tabs.activeId);
        return;
      }
      if ((ctrl && k === "Tab") || (e.altKey && (k === "ArrowRight" || k === "ArrowLeft"))) {
        e.preventDefault();
        tabs.cycle(e.shiftKey || k === "ArrowLeft" ? -1 : 1);
        return;
      }
      if (overlay) return;
      if (k === "Escape" && !e.defaultPrevented) {
        if (dispatchToTab(tabs.activeId, "cancel")) e.preventDefault();
        return;
      }
      if (typing) return;
      if (k === "Delete") {
        if (dispatchToTab(tabs.activeId, "delete")) e.preventDefault();
        return;
      }
      if (k === "?") {
        e.preventDefault();
        ui.setShortcuts(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
}
